import React from 'react';
import { API_BASE } from '../../services/api';
import { Tag, Size, ArtistProfile } from '../../types';

interface AspectRatio {
  id: number;
  name: string;
  ratio?: number;
  defaultSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number }>;
  additionalSizes?: Array<{ id: number; sizeId?: number; size?: Size; effectivePrice?: number }>;
}

interface SizePriceOverride {
  size: string;
  width: string;
  height: string;
  price: string;
  isOriginal: boolean;
}

interface ArtworkFormData {
  title: string;
  artistName: string;
  artistId: string;
  description: string;
  price: string;
  imageUrl: string;
  aspectRatioId: string;
  hasOriginal: boolean;
  isOriginalAvailable: boolean;
  originalSize: string;
  originalWidth: string;
  originalHeight: string;
  originalPrice: string;
  tagIds: number[];
  sizePrices: SizePriceOverride[];
  imageFile?: File;
}

interface ArtworkFormProps {
  formData: ArtworkFormData;
  tags: Tag[];
  allSizes: Size[];
  artistOptions?: ArtistProfile[];
  isArtist?: boolean;
  selectedAspectRatio: AspectRatio | null;
  originalSizeId: string;
  uploading: boolean;
  onFormDataChange: (data: Partial<ArtworkFormData>) => void;
  onOriginalSizeIdChange: (sizeId: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTagToggle: (tagId: number) => void;
  onAddSizePriceOverride: () => void;
  onRemoveSizePriceOverride: (index: number) => void;
  onUpdateSizePriceOverride: (index: number, field: keyof SizePriceOverride, value: string) => void;
}

const ArtworkForm: React.FC<ArtworkFormProps> = ({
  formData,
  tags,
  allSizes,
  artistOptions = [],
  isArtist = false,
  selectedAspectRatio,
  originalSizeId,
  uploading,
  onFormDataChange,
  onOriginalSizeIdChange,
  onFileUpload,
  onTagToggle,
  onAddSizePriceOverride,
  onRemoveSizePriceOverride,
  onUpdateSizePriceOverride
}) => {
  const tagTypes = ['Theme', 'Style', 'Feeling'];
  const tagsByType = tagTypes.reduce((acc, type) => {
    acc[type] = tags.filter(t => t.type === type);
    return acc;
  }, {} as Record<string, Tag[]>);

  return (
    <form className="artwork-form">
      <h2>Create Artwork</h2>
      
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => onFormDataChange({ title: e.target.value })}
          required
        />
      </div>

      <div className="form-row">
        {!isArtist ? (
          <div className="form-group">
            <label>Artist *</label>
            <select
              value={formData.artistId || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                onFormDataChange({ artistId: selectedId });
              }}
              required
            >
              <option value="">Select an artist...</option>
              {artistOptions.map((artist) => (
                <option key={artist.userId} value={artist.userId}>
                  {artist.displayName || artist.email}
                </option>
              ))}
            </select>
            <small>Admins can upload on behalf of any artist.</small>
          </div>
        ) : null}

        <div className="form-group">
          <label>Legacy/Fallback Price *</label>
          <input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => onFormDataChange({ price: e.target.value })}
            required
            placeholder="Used if no aspect ratio set"
          />
        </div>
      </div>

      {formData.aspectRatioId && (
        <div className="form-group">
          <label>Aspect Ratio (Auto-detected)</label>
          <input
            type="text"
            value={selectedAspectRatio ? `${selectedAspectRatio.name} (Ratio: ${selectedAspectRatio.ratio})` : 'Calculating...'}
            disabled
            className="readonly-input"
          />
          {selectedAspectRatio && (
            <div className="aspect-ratio-info">
              <p>Available sizes for {selectedAspectRatio.name}:</p>
              <ul>
                {[...(selectedAspectRatio.defaultSizes || []), ...(selectedAspectRatio.additionalSizes || [])]
                  .filter(spd => spd && spd.size)
                  .map(spd => (
                    <li key={spd.size!.id}>
                      {spd.size!.name} ({spd.size!.width}" × {spd.size!.height}") - ${(spd.effectivePrice || spd.size!.price || 0).toFixed(2)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label>Maximum Size</label>
        <select
          value={originalSizeId}
          onChange={(e) => onOriginalSizeIdChange(e.target.value)}
          required
        >
          <option value="">Select maximum size...</option>
          {allSizes.map(size => (
            <option key={size.id} value={size.id}>
              {size.name} ({size.width}" × {size.height}")
            </option>
          ))}
        </select>
        <small>Select the maximum size available. For physical originals, this is the original piece size. For digital art, this is the maximum print size. The aspect ratio will be automatically calculated.</small>
      </div>

      {formData.originalSize && (
        <div className="original-piece-config">
          <h3>Original Piece (if applicable)</h3>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOriginalAvailable}
                onChange={(e) => onFormDataChange({ isOriginalAvailable: e.target.checked })}
              />
              Original Piece is Available for Purchase
            </label>
            <small>Check this if there's a physical original piece available for sale. Uncheck if it's digital only or the original has been sold.</small>
          </div>
          {formData.isOriginalAvailable && (
            <div className="form-group">
              <label>Original Piece Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.originalPrice}
                onChange={(e) => onFormDataChange({ originalPrice: e.target.value })}
                placeholder="0.00"
                required
              />
              <small>Price for the original physical piece</small>
            </div>
          )}
        </div>
      )}
      
      {formData.originalSize && !selectedAspectRatio && (
        <div className="help-text">
          <p>Calculating aspect ratio...</p>
        </div>
      )}

      <div className="size-price-overrides">
        <h3>Size Price Overrides (Optional)</h3>
        <p className="help-text">Override default prices for specific sizes. Leave empty to use defaults.</p>
        {formData.sizePrices.map((sp, index) => (
          <div key={index} className="size-price-override-row">
            <input
              type="text"
              placeholder="Size (e.g., 5x10)"
              value={sp.size}
              onChange={(e) => onUpdateSizePriceOverride(index, 'size', e.target.value)}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Width"
              value={sp.width}
              onChange={(e) => onUpdateSizePriceOverride(index, 'width', e.target.value)}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Height"
              value={sp.height}
              onChange={(e) => onUpdateSizePriceOverride(index, 'height', e.target.value)}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Price"
              value={sp.price}
              onChange={(e) => onUpdateSizePriceOverride(index, 'price', e.target.value)}
            />
            <button
              type="button"
              onClick={() => onRemoveSizePriceOverride(index)}
              className="btn btn-danger btn-small"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ))}
        <button type="button" onClick={onAddSizePriceOverride} className="btn btn-outline btn-small">
          <i className="fas fa-plus"></i> Add Override
        </button>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => onFormDataChange({ description: e.target.value })}
          rows={4}
        />
      </div>

      <div className="form-group">
        <label>Image *</label>
        <div className="file-upload-group">
          <input
            type="file"
            accept="image/*"
            onChange={onFileUpload}
            disabled={uploading}
            required={!formData.imageUrl}
          />
          <input
            type="text"
            value={formData.imageUrl}
            onChange={(e) => onFormDataChange({ imageUrl: e.target.value })}
            placeholder="Or enter image URL"
          />
        </div>
        {(formData.imageUrl || formData.imageFile) && (
          <img 
            src={formData.imageFile 
              ? URL.createObjectURL(formData.imageFile)
              : (formData.imageUrl.startsWith('http') ? formData.imageUrl : `${API_BASE}${formData.imageUrl}`)} 
            alt="Artwork preview" 
            className="image-preview" 
          />
        )}
      </div>

      <div className="form-group">
        <label>Tags</label>
        {tagTypes.map(type => (
          <div key={type} className="tag-group">
            <h4>{type}</h4>
            <div className="tags-list">
              {tagsByType[type]?.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => onTagToggle(tag.id)}
                  className={`tag-button ${formData.tagIds.includes(tag.id) ? 'active' : ''}`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </form>
  );
};

export default ArtworkForm;
